import { useQuery } from '@tanstack/react-query';
import {
  getSubscriptionStatus,
  getSubscriptionPlans,
  getSubscriptionUsage,
} from '../api/subscriptionApi';

/**
 * Subscription state for the authenticated app. Ported from the mobile hook so
 * the derived flags (isTrialing / daysRemaining / accessStatus / isLocked /
 * isRestricted) match across platforms. Backs the global SubscriptionStatusBanner
 * and (later) the My Plan / All Plans screens.
 */
export function useSubscription() {
  const statusQuery = useQuery({
    queryKey: ['subscription', 'status'],
    queryFn: getSubscriptionStatus,
  });

  const plansQuery = useQuery({
    queryKey: ['subscription', 'plans'],
    queryFn: getSubscriptionPlans,
  });

  const usageQuery = useQuery({
    queryKey: ['subscription', 'usage'],
    queryFn: getSubscriptionUsage,
  });

  const subscription = statusQuery.data;
  const isTrialing = subscription?.status === 'trialing';
  // Prefer the server-computed value (avoids client-clock skew); fall back to local math (FR-056/T166).
  const daysRemaining =
    subscription?.trialDaysRemaining ??
    (subscription?.trialEndDate
      ? Math.max(
          0,
          Math.ceil((new Date(subscription.trialEndDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24)),
        )
      : 0);
  const accessStatus = subscription?.accessStatus;

  return {
    subscription,
    plans: plansQuery.data?.plans ?? [],
    usage: usageQuery.data?.quotas ?? [],
    isLoading: statusQuery.isLoading || plansQuery.isLoading,
    isTrialing,
    daysRemaining,
    accessStatus,
    isLocked: accessStatus === 'locked',
    isRestricted: accessStatus === 'restricted',
    currentPlan: subscription?.plan,
  };
}
